import React, { useState, useEffect } from "react";
import { fetchCategories, createCategory, updateCategory, deleteCategory, resolveImg } from "../../services/api";
import { useContent } from "../../context/ContentContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../ui/alert-dialog";
import { useToast } from "../../hooks/use-toast";
import ImageUploader from "./ImageUploader";
import { Loader2, Pencil, Trash2, Plus } from "lucide-react";

const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const empty = { slug: "", name: "", short: "", tagline: "", description: "", image: "", subcategories: [], order: 999 };

const AdminCategories = () => {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const { toast } = useToast();
  const { refreshCategories } = useContent();

  const load = () => { setLoading(true); fetchCategories().then(setCats).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing("new"); setForm({ ...empty, order: (cats.length + 1) }); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c, subcategories: c.subcategories || [] }); };

  const save = async () => {
    if (!form.name) { toast({ title: "Name required", variant: "destructive" }); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await createCategory({ ...form, slug: form.slug || slugify(form.name) });
        toast({ title: "Category created" });
      } else {
        await updateCategory(editing.slug, form);
        toast({ title: "Category updated" });
      }
      setEditing(null); load(); refreshCategories();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await deleteCategory(delTarget.slug); toast({ title: "Category deleted" }); setDelTarget(null); load(); refreshCategories(); }
    catch (e) { toast({ title: "Delete failed", variant: "destructive" }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display font-bold text-lg text-[#14171c]">Product Categories</h2>
        <Button onClick={openNew} className="bg-[#C9A227] hover:bg-[#A6841B] text-white"><Plus className="w-4 h-4 mr-1" /> New Category</Button>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {cats.map((c) => (
            <div key={c.slug} className="bg-white rounded-xl border border-neutral-200 overflow-hidden flex">
              <img src={resolveImg(c.image)} alt={c.name} className="w-28 h-auto object-cover flex-shrink-0" />
              <div className="p-4 flex-1">
                <div className="font-display font-bold text-[#14171c]">{c.name}</div>
                <div className="text-xs text-neutral-500 mb-1">/{c.slug}</div>
                <div className="text-xs text-neutral-500">{(c.subcategories || []).length} subcategories</div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="h-8 text-xs"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => setDelTarget(c)} className="h-8 w-8 p-0 text-red-500 border-red-200"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing === "new" ? "New Category" : "Edit Category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Name</Label><Input className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
            {editing === "new" && <div><Label>Slug (URL)</Label><Input className="mt-1.5" value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder={slugify(form.name) || "auto-generated"} /></div>}
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Short Name</Label><Input className="mt-1.5" value={form.short} onChange={(e) => set("short", e.target.value)} /></div>
              <div><Label>Order</Label><Input type="number" className="mt-1.5" value={form.order} onChange={(e) => set("order", Number(e.target.value))} /></div>
            </div>
            <div><Label>Tagline</Label><Input className="mt-1.5" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} /></div>
            <ImageUploader label="Category Image" value={form.image} onChange={(v) => set("image", v)} />
            <div><Label>Description</Label><Textarea className="mt-1.5" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div><Label>Subcategories (one per line)</Label><Textarea className="mt-1.5" rows={4} value={(form.subcategories || []).join("\n")} onChange={(e) => set("subcategories", e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-[#C9A227] hover:bg-[#A6841B] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Delete category?</AlertDialogTitle><AlertDialogDescription>This removes "{delTarget?.name}" from the menu. Products in it will remain but won't have a category page.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminCategories;
